// Landings de cartelera por ciudad y momento (28-10-2026, patrón
// agendaLandings.ts de RE:PÚBLICO): /{ciudad}/hoy, /fin-de-semana,
// /version-original y /sesiones-especiales. Definidas en un solo sitio para
// la SPA (Movies.tsx), el prerender, el sitemap y llms.txt. Una landing solo
// se indexa (y entra en el sitemap) con LANDING_MIN_ITEMS películas o más;
// con menos existe pero con noindex.
//
// Sin imports y sin enums: el prerender la carga bajo type-stripping de Node.

export type LandingSlug = "hoy" | "fin-de-semana" | "version-original" | "sesiones-especiales" | "proximamente";

export const LANDING_SLUGS: readonly LandingSlug[] = ["hoy", "fin-de-semana", "version-original", "sesiones-especiales", "proximamente"];

export const LANDING_MIN_ITEMS = 6;

/** Etiquetas de pase (screening_tags) que hacen «especial» una sesión. */
export const SPECIAL_TAGS: readonly string[] = [
  "coloquio",
  "presentacion",
  "preestreno",
  "musica_directo",
  "cineclub",
  "35mm",
  "70mm",
  "16mm",
  "restaurada",
];

export const landingPath = (citySlug: string, slug: LandingSlug) => `/${citySlug}/${slug}`;

/** Textos en castellano para el prerender (la SPA usa las claves `landings.*`). */
export const LANDING_TEXT_ES: Record<
  LandingSlug,
  { label: string; title: string; titleTail?: string; description: string; intro: string }
> = {
  hoy: {
    label: "Hoy",
    title: "Cine en {{city}} hoy: películas y horarios",
    description: "Qué ponen hoy en los cines independientes de {{city}}: todas las sesiones de hoy en {{cinemas}}.",
    intro: "Las sesiones de hoy en las salas de autor de {{city}}, ordenadas por hora.",
  },
  "fin-de-semana": {
    label: "Fin de semana",
    title: "Cine este fin de semana en {{city}}",
    titleTail: "películas y horarios",
    description: "Películas y horarios de viernes a domingo en los cines independientes de {{city}}: {{cinemas}}.",
    intro: "Lo que ponen de viernes a domingo las salas independientes de {{city}}.",
  },
  "version-original": {
    label: "Versión original",
    title: "Cine en versión original en {{city}}",
    titleTail: "VOSE y horarios",
    description: "Películas en versión original subtitulada esta semana en los cines independientes de {{city}}: {{cinemas}}.",
    intro: "Todas las sesiones en versión original de la semana en las salas de autor de {{city}}.",
  },
  "sesiones-especiales": {
    label: "Sesiones especiales",
    title: "Sesiones especiales de cine en {{city}}",
    titleTail: "coloquios, 35 mm, preestrenos",
    description: "Coloquios, pases en 35 y 70 mm, preestrenos y cineclubs esta semana en los cines independientes de {{city}}.",
    intro: "Las sesiones que no son un pase más: con coloquio, en celuloide, preestrenos y cineclubs.",
  },
  proximamente: {
    label: "Próximamente",
    title: "Próximos estrenos de cine en {{city}}",
    titleTail: "fechas y cines",
    description: "Las películas que llegan a los cines independientes de {{city}} después de esta semana, con sus primeras fechas: {{cinemas}}.",
    intro: "Lo que llega a las salas de {{city}} después de esta semana, con su primera fecha.",
  },
};

/** Último día de «esta semana» (hoy + 6), el horizonte de la cartelera. */
export function weekEnd(today: string): string {
  const [y, m, d] = today.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 6, 12)).toISOString().slice(0, 10);
}

/**
 * «Próximamente» (29-10-2026) va por película, no por pase: entran las que NO
 * tienen ningún pase esta semana en la ciudad y sí más adelante. El matcher
 * de pase deja los posteriores a esta semana; esta función dice qué
 * películas quedan fuera porque ya están en cartel (clave `ciudad|película`).
 */
export function inThisWeek(
  screenings: { screening_date: string; movie_id: string; city?: string | null }[],
  today: string,
): Set<string> {
  const end = weekEnd(today);
  const out = new Set<string>();
  for (const s of screenings) if (s.screening_date <= end) out.add(`${s.city ?? ""}|${s.movie_id}`);
  return out;
}

export const fillLanding = (tpl: string, vars: Record<string, string>) =>
  tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? "");

/** Viernes–domingo: los de esta semana si estamos en ellos, si no los próximos. */
export function weekendDates(today: string): string[] {
  const [y, m, d] = today.split("-").map(Number);
  const base = Date.UTC(y, m - 1, d, 12);
  const dow = new Date(base).getUTCDay(); // 0 = domingo
  const toFriday = dow >= 5 ? 0 : dow === 0 ? 0 : 5 - dow;
  const start = dow === 0 ? 0 : dow >= 5 ? 0 : toFriday;
  const endOffset = dow === 0 ? 0 : dow >= 5 ? 7 - dow : toFriday + 2;
  const out: string[] = [];
  for (let i = start; i <= endOffset; i++) {
    out.push(new Date(base + i * 86_400_000).toISOString().slice(0, 10));
  }
  return out;
}

export type LandingScreening = { screening_date: string; version?: string | null; tags?: string[] | null };

export function landingMatcher(slug: LandingSlug, today: string): (s: LandingScreening) => boolean {
  switch (slug) {
    case "hoy":
      return (s) => s.screening_date === today;
    case "fin-de-semana": {
      const set = new Set(weekendDates(today));
      return (s) => set.has(s.screening_date);
    }
    case "version-original":
      return (s) => (s.version ?? "").toLowerCase() !== "doblada";
    case "sesiones-especiales":
      return (s) => (s.tags ?? []).some((t) => SPECIAL_TAGS.includes(t));
    case "proximamente": {
      const end = weekEnd(today);
      return (s) => s.screening_date > end;
    }
  }
}
